import React, { useEffect, useState, useCallback } from 'react';
import API, { fileUrl, downloadFile } from '../utils/api';

/* ── Palette ── */
const V1 = '#7F00FF', V2 = '#B163FF';

/* ── Tiny SVG Icons ── */
const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const ExternalIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ── Review Modal ── */
const ReviewModal = ({ app, onClose }) => {
  const [lightbox, setLightbox] = useState(null);
  const tasks = (app.taskSubmissions || []).slice().sort((a, b) => a.taskIndex - b.taskIndex);

  return (
    <div className="rvm-overlay" onClick={onClose}>
      <style>{`
        .rvm-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.8); z-index: 6000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .rvm-modal { width: 100%; max-width: 900px; max-height: 90vh; background: #1b1030; border: 1px solid #3a2a5c; border-radius: 18px; display: flex; flex-direction: column; overflow: hidden; }
        .rvm-header { padding: 20px 26px; border-bottom: 1px solid #3a2a5c; display: flex; align-items: center; justify-content: space-between; }
        .rvm-id { font-family: monospace; font-size: 11px; color: #B163FF; font-weight: 700; margin: 0 0 4px; }
        .rvm-title { font-size: 18px; font-weight: 800; margin: 0; color: #ffffff; }
        .rvm-course { color: #a89bc4; font-size: 13px; margin: 4px 0 0; }
        .rvm-close { background: #2a1c47; border: 1px solid #3a2a5c; width: 34px; height: 34px; border-radius: 8px; color: #cbbfe0; cursor: pointer; font-size: 16px; }
        .rvm-body { flex: 1; overflow-y: auto; padding: 20px 26px; display: flex; flex-direction: column; gap: 14px; }
        .rvm-empty { text-align: center; padding: 50px 20px; color: #a89bc4; font-size: 14px; }
        .rvm-task { background: #241639; border: 1px solid #3a2a5c; border-radius: 14px; padding: 16px 18px; }
        .rvm-task-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
        .rvm-task-num { width: 30px; height: 30px; border-radius: 8px; background: rgba(177,99,255,0.18); color: #cba8ff; font-weight: 800; font-size: 13px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .rvm-task-name { font-size: 15px; font-weight: 700; color: #ffffff; }
        .rvm-task-link { margin-bottom: 12px; }
        .rvm-link { color: #cba8ff; font-size: 13px; word-break: break-all; text-decoration: underline; }
        .rvm-nolink { color: #a89bc4; font-size: 13px; font-style: italic; }
        .rvm-shots { display: flex; flex-wrap: wrap; gap: 10px; }
        .rvm-shot { width: 140px; height: 100px; object-fit: cover; border-radius: 8px; border: 1px solid #3a2a5c; cursor: pointer; }
        .rvm-noshot { color: #a89bc4; font-size: 13px; font-style: italic; }
        .rvm-lightbox { position: fixed; inset: 0; background: rgba(0,0,0,0.92); z-index: 7000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .rvm-lightbox img { max-width: min(95vw,1400px); max-height: 92vh; border-radius: 10px; object-fit: contain; }
      `}</style>

      <div className="rvm-modal" onClick={e => e.stopPropagation()}>
        <div className="rvm-header">
          <div>
            <p className="rvm-id">{app.internshipId}</p>
            <h2 className="rvm-title">{app.name} — Submission Review</h2>
            <p className="rvm-course">{app.courseName}</p>
          </div>
          <button className="rvm-close" onClick={onClose}>✕</button>
        </div>

        <div className="rvm-body">
          {tasks.length === 0 && (
            <div className="rvm-empty">No task submissions found for this student yet.</div>
          )}

          {tasks.map((task) => (
            <div className="rvm-task" key={task.taskIndex}>
              <div className="rvm-task-head">
                <span className="rvm-task-num">{task.taskIndex + 1}</span>
                <span className="rvm-task-name">{task.topicName || `Task ${task.taskIndex + 1}`}</span>
              </div>

              <div className="rvm-task-link">
                {task.githubLink ? (
                  <a href={task.githubLink} target="_blank" rel="noreferrer" className="rvm-link">
                    {task.githubLink}
                  </a>
                ) : (
                  <span className="rvm-nolink">No GitHub link submitted</span>
                )}
              </div>

              <div className="rvm-shots">
                {task.screenshots && task.screenshots.length > 0 ? (
                  task.screenshots.map((src, si) => (
                    <img
                      key={si}
                      src={fileUrl(src)}
                      alt={`screenshot-${si}`}
                      className="rvm-shot"
                      onClick={() => setLightbox(fileUrl(src))}
                    />
                  ))
                ) : (
                  <span className="rvm-noshot">No screenshots uploaded</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {lightbox && (
        <div className="rvm-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Screenshot" onClick={e => e.stopPropagation()} />

        </div>
      )}
    </div>
  );
};

/* ── Status Config ── */
const STATUS = {
  pending_payment:   { label: 'Pending Payment',   color: '#F59E0B', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.25)'  },
  in_progress:       { label: 'In Progress',       color: '#3B82F6', bg: 'rgba(59,130,246,0.1)',   border: 'rgba(59,130,246,0.25)'  },
  under_review:      { label: 'Under Review',      color: '#A855F7', bg: 'rgba(168,85,247,0.1)',   border: 'rgba(168,85,247,0.25)'  },
  changes_requested: { label: 'Changes Requested', color: '#F87171', bg: 'rgba(248,113,113,0.1)',  border: 'rgba(248,113,113,0.25)' },
  completed:         { label: 'Completed',         color: '#10B981', bg: 'rgba(16,185,129,0.1)',   border: 'rgba(16,185,129,0.25)'  },
  rejected:          { label: 'Rejected',          color: '#F87171', bg: 'rgba(248,113,113,0.1)',  border: 'rgba(248,113,113,0.25)' },
};

const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.pending_payment;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
      {s.label}
    </span>
  );
};

/* ── Stat Card ── */
const StatCard = ({ label, value, color }) => (
  <div style={{
    background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16,
    padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 6,
  }}>
    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</p>
    <p style={{ fontSize: 32, fontWeight: 800, color: color || V2, fontFamily: "'Inter',sans-serif", lineHeight: 1 }}>{value}</p>
  </div>
);

/* ════════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════════ */
const AdminMicrointernships = () => {
  const [pin, setPin]               = useState('');
  const [pinVerified, setPinVerified] = useState(false);
  const [pinError, setPinError]     = useState('');
  const [pinChecking, setPinChecking] = useState(false);

  const [applications, setApplications] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  const [search, setSearch]     = useState('');
  const [filterStatus, setFilter] = useState('all');

  const [drawer, setDrawer]               = useState(null);
  const [showReview, setShowReview]        = useState(false);
  const [certFile, setCertFile]            = useState(null);
  const [certFileName, setCertFileName]    = useState('');
  const [changeNote, setChangeNote]        = useState('');
  const [actionLoading, setActionLoading]  = useState(false);
  const certFileRef = React.useRef();

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinChecking(true);
    setPinError('');
    try {
      await API.post('/microinternships/admin/verify-pin', { pin });
      setPinVerified(true);
    } catch {
      setPinError('Incorrect PIN. Access denied.');
      setPin('');
    } finally {
      setPinChecking(false);
    }
  };

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/microinternships/admin/all');
      setApplications(res.data);
    } catch { setError('Failed to load applications.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (pinVerified) fetchApplications(); }, [pinVerified, fetchApplications]);

  // Re-fetches the full list (without the table "Loading…" flash) and returns
  // the freshest copy of one record — the drawer/review modal can otherwise
  // show a stale snapshot if the student submitted more after the last load.
  const refreshOne = useCallback(async (id) => {
    try {
      const res = await API.get('/microinternships/admin/all');
      setApplications(res.data);
      return res.data.find(a => a._id === id) || null;
    } catch {
      return null;
    }
  }, []);

  const openDrawer = useCallback(async (app) => {
    setDrawer(app);
    setChangeNote('');
    setCertFile(null);
    setCertFileName('');
    const fresh = await refreshOne(app._id);
    if (fresh) setDrawer(fresh);
  }, [refreshOne]);

  const openReview = useCallback(async () => {
    if (!drawer) return;
    const fresh = await refreshOne(drawer._id);
    if (fresh) setDrawer(fresh);
    setShowReview(true);
  }, [drawer, refreshOne]);

  /* ── Actions ── */
  const handleVerify = async (id) => {
    setActionLoading(true);
    try {
      await API.put(`/microinternships/admin/verify-payment/${id}`);
      await fetchApplications();
      if (drawer?._id === id) setDrawer(prev => ({ ...prev, status: 'in_progress' }));
    } catch { alert('Failed to verify payment.'); }
    finally { setActionLoading(false); }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(true);
    try {
      await API.put(`/microinternships/admin/reject-payment/${id}`, { reason });
      await fetchApplications();
      if (drawer?._id === id) setDrawer(prev => ({ ...prev, status: 'rejected', reviewNote: reason || '' }));
    } catch { alert('Failed to reject application.'); }
    finally { setActionLoading(false); }
  };

  const handleIssueCert = async (id) => {
    if (!certFile) { alert('Please select a certificate file (PNG or PDF).'); return; }
    setActionLoading(true);
    try {
      const form = new FormData();
      form.append('certificate', certFile);
      await API.post(`/microinternships/admin/issue-certificate/${id}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchApplications();
      setCertFile(null); setCertFileName('');
      setDrawer(null);
    } catch { alert('Failed to issue certificate.'); }
    finally { setActionLoading(false); }
  };

  const handleRequestChanges = async (id) => {
    if (!changeNote.trim()) { alert('Please write a note telling the student what to fix.'); return; }
    setActionLoading(true);
    try {
      await API.put(`/microinternships/admin/request-changes/${id}`, { note: changeNote.trim() });
      await fetchApplications();
      setChangeNote('');
      setDrawer(null);
    } catch { alert('Failed to request changes.'); }
    finally { setActionLoading(false); }
  };

  const handleCleanupScreenshots = async (id) => {
    if (!window.confirm('Delete all uploaded screenshots for this student from Cloudinary? GitHub links stay intact. This cannot be undone.')) return;
    setActionLoading(true);
    try {
      const res = await API.delete(`/microinternships/admin/cleanup-screenshots/${id}`);
      const fresh = await refreshOne(id);
      if (fresh) setDrawer(fresh);
      alert(`Deleted ${res.data.deletedCount} screenshot${res.data.deletedCount === 1 ? '' : 's'}.`);
    } catch { alert('Failed to clean up screenshots.'); }
    finally { setActionLoading(false); }
  };

  /* ── Derived data ── */
  const filtered = applications.filter(a => {
    const matchSearch =
      a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.email?.toLowerCase().includes(search.toLowerCase()) ||
      a.internshipId?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filterStatus === 'all' || a.status === filterStatus;
    return matchSearch && matchFilter;
  });

  const stats = {
    total:   applications.length,
    pending: applications.filter(a => a.status === 'pending_payment').length,
    active:  applications.filter(a => a.status === 'in_progress').length,
    review:  applications.filter(a => a.status === 'under_review').length,
    done:    applications.filter(a => a.status === 'completed').length,
  };

  /* ════════ PIN GATE ════════ */
  if (!pinVerified) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 400, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 24, padding: '44px 40px', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: `rgba(127,0,255,0.1)`, border: `1px solid rgba(127,0,255,0.2)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 24 }}>🔐</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8, fontFamily: "'Inter',sans-serif" }}>Restricted Access</h2>
        <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>Enter the admin PIN to access the Microinternships control panel.</p>
        <form onSubmit={handlePinSubmit}>
          <input
            type="password" autoFocus required value={pin} onChange={e => setPin(e.target.value)}
            placeholder="• • • •" maxLength={6}
            style={{
              width: '100%', padding: '14px', textAlign: 'center', letterSpacing: '0.4em',
              background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10,
              color: 'var(--text)', fontSize: 22, outline: 'none', marginBottom: 12, boxSizing: 'border-box',
            }}
          />
          {pinError && <p style={{ color: '#F87171', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{pinError}</p>}
          <button type="submit" disabled={pinChecking} style={{
            width: '100%', padding: '14px', background: `linear-gradient(135deg,${V1},${V2})`,
            color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15,
            cursor: pinChecking ? 'not-allowed' : 'pointer', fontFamily: "'Space Grotesk',sans-serif",
            opacity: pinChecking ? 0.7 : 1,
          }}>{pinChecking ? 'Checking...' : 'Unlock'}</button>
        </form>
      </div>
    </div>
  );

  /* ════════ MAIN PANEL ════════ */
  return (
    <>
      <style>{`
        .ami-page { background: var(--bg); color: var(--text); min-height: 100vh; padding: 100px 0 80px; font-family: 'Space Grotesk', sans-serif; }
        .ami-wrap { max-width: 1300px; margin: 0 auto; padding: 0 24px; }
        .ami-topbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; flex-wrap: wrap; gap: 12; }
        .ami-title { font-size: 28px; font-weight: 800; font-family: 'Inter', sans-serif; }
        .ami-subtitle { font-size: 13px; color: var(--muted); margin-top: 2px; }
        .ami-stats { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 14px; margin-bottom: 24px; }
        .ami-toolbar { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; }
        .ami-search { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 220px; background: var(--bg2); border: 1px solid var(--border); border-radius: 10px; padding: 0 14px; }
        .ami-search input { background: none; border: none; outline: none; color: var(--text); font-size: 14px; padding: 11px 0; width: 100%; font-family: inherit; }
        .ami-search svg { color: var(--muted); flex-shrink: 0; }
        .ami-filter { display: flex; gap: 6px; flex-wrap: wrap; }
        .ami-filter-btn { padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; border: 1px solid var(--border); background: var(--bg2); color: var(--muted); font-family: inherit; transition: all 0.15s; }
        .ami-filter-btn.active { background: rgba(127,0,255,0.1); color: var(--v2); border-color: rgba(127,0,255,0.3); }
        .ami-table-wrap { background: var(--bg2); border: 1px solid var(--border); border-radius: 18px; overflow: hidden; }
        .ami-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
        .ami-table thead tr { background: var(--bg3); border-bottom: 1px solid var(--border); }
        .ami-table th { padding: 13px 18px; text-align: left; color: var(--muted); font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; white-space: nowrap; }
        .ami-table tbody tr { border-bottom: 1px solid var(--border); transition: background 0.15s; cursor: pointer; }
        .ami-table tbody tr:last-child { border-bottom: none; }
        .ami-table tbody tr:hover { background: var(--bg3); }
        .ami-table td { padding: 14px 18px; vertical-align: middle; }
        .ami-action-btn { padding: 6px 14px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer; border: none; font-family: inherit; white-space: nowrap; }
        .ami-empty { padding: 60px; text-align: center; color: var(--muted); font-size: 15px; }

        /* Drawer */
        .ami-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.55); backdrop-filter: blur(6px); z-index: 5000; display: flex; justify-content: flex-end; }
        .ami-drawer { width: min(520px, 100vw); height: 100vh; background: var(--bg2); border-left: 1px solid var(--border); display: flex; flex-direction: column; animation: slideInRight 0.3s cubic-bezier(0.16,1,0.3,1); overflow: hidden; }
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .ami-drawer-head { display: flex; align-items: center; justify-content: space-between; padding: 22px 28px; border-bottom: 1px solid var(--border); flex-shrink: 0; }
        .ami-drawer-body { flex: 1; overflow-y: auto; padding: 24px 28px; display: flex; flex-direction: column; gap: 20px; }
        .ami-drawer-body::-webkit-scrollbar { width: 5px; }
        .ami-drawer-body::-webkit-scrollbar-thumb { background: var(--border); border-radius: 999px; }
        .ami-drawer-footer { padding: 18px 28px; border-top: 1px solid var(--border); flex-shrink: 0; }
        .ami-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .ami-info-item { background: var(--bg3); border: 1px solid var(--border); border-radius: 10px; padding: 12px 14px; }
        .ami-info-label { font-size: 10px; font-weight: 800; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px; }
        .ami-info-value { font-size: 13px; font-weight: 600; color: var(--text); word-break: break-all; }
        .ami-section-head { font-size: 11px; font-weight: 800; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 10px; }
        .ami-cert-input { width: 100%; padding: 12px 14px; background: var(--bg3); border: 1px solid var(--border); border-radius: 10px; color: var(--text); font-size: 14px; outline: none; font-family: inherit; box-sizing: border-box; transition: border-color 0.2s; }
        .ami-cert-input:focus { border-color: rgba(127,0,255,0.5); }
        .ami-primary-btn { width: 100%; padding: 14px; background: linear-gradient(135deg, ${V1}, ${V2}); color: #fff; border: none; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; transition: opacity 0.2s; }
        .ami-primary-btn:hover { opacity: 0.88; }
        .ami-primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ami-verify-btn { width: 100%; padding: 14px; background: rgba(59,130,246,0.1); color: #3B82F6; border: 1px solid rgba(59,130,246,0.3); border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; transition: all 0.2s; }
        .ami-verify-btn:hover { background: rgba(59,130,246,0.2); }
        .ami-verify-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ami-screenshot { width: 100%; border-radius: 10px; border: 1px solid var(--border); object-fit: contain; background: var(--bg3); cursor: zoom-in; }
        .ami-refresh-btn { display: flex; align-items: center; gap: 7px; padding: 9px 16px; background: var(--bg2); border: 1px solid var(--border); border-radius: 9px; color: var(--muted); font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.15s; }
        .ami-refresh-btn:hover { color: var(--text); border-color: var(--border-strong); }
      `}</style>

      <div className="ami-page">
        <div className="ami-wrap">

          {/* Top bar */}
          <div className="ami-topbar">
            <div>
              <h1 className="ami-title">Microinternships</h1>
              <p className="ami-subtitle">Manage student enrollments, payments, and certificates</p>
            </div>
            <button className="ami-refresh-btn" onClick={fetchApplications}>
              <RefreshIcon /> Refresh
            </button>
          </div>

          {/* Stats */}
          <div className="ami-stats">
            <StatCard label="Total"        value={stats.total}   color={V2} />
            <StatCard label="Pending Pay"  value={stats.pending} color="#F59E0B" />
            <StatCard label="In Progress"  value={stats.active}  color="#3B82F6" />
            <StatCard label="Under Review" value={stats.review}  color="#A855F7" />
            <StatCard label="Completed"    value={stats.done}    color="#10B981" />
          </div>

          {/* Toolbar */}
          <div className="ami-toolbar">
            <div className="ami-search">
              <SearchIcon />
              <input
                placeholder="Search by name, email or ID…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="ami-filter">
              {['all', 'pending_payment', 'in_progress', 'under_review', 'changes_requested', 'completed', 'rejected'].map(s => (
                <button
                  key={s}
                  className={`ami-filter-btn ${filterStatus === s ? 'active' : ''}`}
                  onClick={() => setFilter(s)}
                >
                  {s === 'all' ? 'All' : STATUS[s]?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          {error && <p style={{ color: '#F87171', marginBottom: 16, fontWeight: 600 }}>{error}</p>}

          <div className="ami-table-wrap">
            {loading ? (
              <div className="ami-empty">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="ami-empty">No applications match your filters.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="ami-table">
                  <thead>
                    <tr>
                      {['ID', 'Student', 'Course', 'College', 'Payment', 'Status', 'Enrolled', 'Action'].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(app => (
                      <tr key={app._id} onClick={() => openDrawer(app)}>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, color: V2, fontWeight: 700 }}>{app.internshipId}</span>
                        </td>
                        <td>
                          <div style={{ fontWeight: 700 }}>{app.name}</div>
                          <div style={{ color: 'var(--muted)', fontSize: 12 }}>{app.email}</div>
                        </td>
                        <td style={{ color: 'var(--text-2)', maxWidth: 160 }}>{app.courseName}</td>
                        <td style={{ color: 'var(--muted)', fontSize: 12 }}>{app.college}<br/><span style={{ color: 'var(--muted)' }}>{app.year} year</span></td>
                        <td style={{ fontSize: 12, color: 'var(--muted)' }}>UTR: {app.transactionId}</td>
                        <td><StatusPill status={app.status} /></td>
                        <td style={{ color: 'var(--muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {new Date(app.createdAt).toLocaleDateString('en-IN')}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          {app.status === 'pending_payment' && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="ami-action-btn" style={{ background: 'rgba(59,130,246,0.1)', color: '#3B82F6' }}
                                onClick={() => handleVerify(app._id)}>
                                Verify Pay
                              </button>
                              <button className="ami-action-btn" style={{ background: 'rgba(248,113,113,0.1)', color: '#F87171' }}
                                onClick={() => {
                                  const reason = window.prompt('Reason for rejecting this payment? (optional)');
                                  if (reason === null) return; // cancelled
                                  handleReject(app._id, reason);
                                }}>
                                Reject
                              </button>
                            </div>
                          )}
                          {app.status === 'under_review' && (
                            <button className="ami-action-btn" style={{ background: 'rgba(168,85,247,0.1)', color: '#A855F7' }}
                              onClick={() => openDrawer(app)}>
                              Review
                            </button>
                          )}
                          {app.status === 'completed' && (
                            <button
                              onClick={() => downloadFile(fileUrl(app.certificateUrl), `ThiranX_Certificate_${app.internshipId}.${app.certificateFormat || 'pdf'}`)}
                              style={{ fontSize: 12, color: '#10B981', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                              View Cert <ExternalIcon />
                            </button>
                          )}
                          {app.status === 'in_progress' && (
                            <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>Awaiting submit</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Side Drawer ── */}
      {drawer && (
        <div className="ami-overlay" onClick={() => setDrawer(null)}>
          <div className="ami-drawer" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="ami-drawer-head">
              <div>
                <p style={{ fontFamily: 'monospace', fontSize: 12, color: V2, fontWeight: 700, marginBottom: 2 }}>{drawer.internshipId}</p>
                <h3 style={{ fontSize: 17, fontWeight: 800 }}>{drawer.name}</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <StatusPill status={drawer.status} />
                <button onClick={() => setDrawer(null)} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', width: 34, height: 34, borderRadius: 8, color: 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CloseIcon />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="ami-drawer-body">

              {/* Info grid */}
              <div>
                <p className="ami-section-head">Student Details</p>
                <div className="ami-info-grid">
                  <div className="ami-info-item"><p className="ami-info-label">Email</p><p className="ami-info-value">{drawer.email}</p></div>
                  <div className="ami-info-item"><p className="ami-info-label">College</p><p className="ami-info-value">{drawer.college}</p></div>
                  <div className="ami-info-item"><p className="ami-info-label">Year</p><p className="ami-info-value">{drawer.year}</p></div>
                  <div className="ami-info-item"><p className="ami-info-label">Enrolled</p><p className="ami-info-value">{new Date(drawer.createdAt).toLocaleDateString('en-IN')}</p></div>
                </div>
              </div>

              {/* Course */}
              <div>
                <p className="ami-section-head">Course</p>
                <div className="ami-info-item" style={{ background: 'rgba(127,0,255,0.06)', borderColor: 'rgba(127,0,255,0.2)' }}>
                  <p className="ami-info-value" style={{ color: V2 }}>{drawer.courseName}</p>
                </div>
              </div>

              {/* Payment */}
              <div>
                <p className="ami-section-head">Payment</p>
                <div className="ami-info-item" style={{ marginBottom: 10 }}>
                  <p className="ami-info-label">UPI Transaction ID / UTR</p>
                  <p className="ami-info-value" style={{ fontFamily: 'monospace', color: '#F59E0B' }}>{drawer.transactionId}</p>
                </div>
                {drawer.paymentScreenshot && (
                  <a href={fileUrl(drawer.paymentScreenshot)} target="_blank" rel="noreferrer">
                    <img
                      src={fileUrl(drawer.paymentScreenshot)}
                      alt="Payment screenshot"
                      className="ami-screenshot"
                    />
                  </a>
                )}
              </div>

              {/* Review feedback (if any was sent) */}
              {drawer.reviewNote && (
                <div>
                  <p className="ami-section-head">Feedback Sent to Student</p>
                  <div className="ami-info-item" style={{ background: 'rgba(248,113,113,0.06)', borderColor: 'rgba(248,113,113,0.2)' }}>
                    <p className="ami-info-value" style={{ color: '#F87171', fontWeight: 500 }}>{drawer.reviewNote}</p>
                  </div>
                </div>
              )}

              {/* GitHub repo (if submitted) */}
              {drawer.githubRepo && (
                <div>
                  <p className="ami-section-head">Submitted GitHub Repo</p>
                  <div className="ami-info-item">
                    <a href={drawer.githubRepo} target="_blank" rel="noreferrer"
                      style={{ color: V2, fontSize: 13, wordBreak: 'break-all', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      {drawer.githubRepo} <ExternalIcon />
                    </a>
                  </div>
                </div>
              )}

              {/* Certificate (if done) */}
              {drawer.status === 'completed' && drawer.certificateUrl && (
                <div>
                  <p className="ami-section-head">Certificate</p>
                  <div className="ami-info-item">
                    <button
                      onClick={() => downloadFile(fileUrl(drawer.certificateUrl), `ThiranX_Certificate_${drawer.internshipId}.${drawer.certificateFormat || 'pdf'}`)}
                      style={{ color: '#10B981', fontSize: 13, wordBreak: 'break-all', display: 'flex', alignItems: 'flex-start', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0, textAlign: 'left' }}>
                      {drawer.certificateUrl} <ExternalIcon />
                    </button>
                  </div>
                </div>
              )}

            </div>

            {/* Footer — action area */}
            <div className="ami-drawer-footer">
              {drawer.status === 'pending_payment' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button className="ami-verify-btn" disabled={actionLoading} onClick={() => handleVerify(drawer._id)}>
                    {actionLoading ? 'Processing…' : '✓ Verify Payment & Unlock Tasks'}
                  </button>

                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
                    <p style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600, margin: '0 0 8px' }}>Payment invalid or fake? Reject with a reason:</p>
                    <textarea
                      value={changeNote}
                      onChange={e => setChangeNote(e.target.value)}
                      placeholder="e.g. Screenshot doesn't match transaction ID…"
                      rows={2}
                      style={{ width: '100%', padding: '10px 12px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                    <button
                      disabled={actionLoading}
                      onClick={() => { handleReject(drawer._id, changeNote.trim()); setChangeNote(''); }}
                      style={{ width: '100%', marginTop: 8, padding: '12px', background: 'rgba(248,113,113,0.08)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', opacity: actionLoading ? 0.5 : 1 }}
                    >
                      {actionLoading ? 'Rejecting…' : '✕ Reject Application'}
                    </button>
                  </div>
                </div>
              )}

              {drawer.status === 'under_review' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    onClick={openReview}
                    style={{ width: '100%', padding: '13px', background: 'rgba(168,85,247,0.08)', color: '#A855F7', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 4 }}
                  >
                    View Task Submissions &amp; Screenshots
                  </button>

                  <p style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600, margin: 0 }}>Upload certificate (PNG or PDF):</p>
                  <input
                    ref={certFileRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.pdf"
                    style={{ display: 'none' }}
                    onChange={e => {
                      const f = e.target.files[0];
                      if (f) { setCertFile(f); setCertFileName(f.name); }
                    }}
                  />
                  <div
                    onClick={() => certFileRef.current?.click()}
                    style={{
                      border: '1.5px dashed var(--border)', borderRadius: 10, padding: '14px 16px',
                      display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                      background: certFile ? 'rgba(16,185,129,0.05)' : 'var(--bg3)',
                      borderColor: certFile ? 'rgba(16,185,129,0.4)' : 'var(--border)',
                      transition: 'all 0.2s',
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{certFile ? (certFileName.endsWith('.pdf') ? '📄' : '🖼️') : '📎'}</span>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: certFile ? '#10B981' : 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {certFileName || 'Click to select file'}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--muted)', margin: '2px 0 0' }}>PNG, JPG or PDF · max 20 MB</p>
                    </div>
                    {certFile && <button onClick={e => { e.stopPropagation(); setCertFile(null); setCertFileName(''); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>✕</button>}
                  </div>

                  <button className="ami-primary-btn" disabled={actionLoading || !certFile} onClick={() => handleIssueCert(drawer._id)}>
                    {actionLoading ? 'Uploading & Issuing…' : '✓ Approve & Issue Certificate'}
                  </button>

                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
                    <p style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600, margin: '0 0 8px' }}>Something wrong? Send it back with a note:</p>
                    <textarea
                      value={changeNote}
                      onChange={e => setChangeNote(e.target.value)}
                      placeholder="e.g. Task 3 screenshot is missing, please re-upload…"
                      rows={3}
                      style={{ width: '100%', padding: '10px 12px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                    <button
                      disabled={actionLoading || !changeNote.trim()}
                      onClick={() => handleRequestChanges(drawer._id)}
                      style={{ width: '100%', marginTop: 8, padding: '12px', background: 'rgba(248,113,113,0.08)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', opacity: (actionLoading || !changeNote.trim()) ? 0.5 : 1 }}
                    >
                      {actionLoading ? 'Sending…' : '↩ Request Changes'}
                    </button>
                  </div>
                </div>
              )}

              {drawer.status === 'in_progress' && (
                <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, fontWeight: 600 }}>
                  Student is working on tasks. No action needed yet.
                </p>
              )}

              {drawer.status === 'changes_requested' && (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#F87171', fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>
                    Changes requested — waiting on student to resubmit.
                  </p>
                  {drawer.reviewNote && (
                    <p style={{ color: 'var(--muted)', fontSize: 12, fontStyle: 'italic', margin: 0 }}>"{drawer.reviewNote}"</p>
                  )}
                </div>
              )}

              {drawer.status === 'rejected' && (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#F87171', fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>
                    Application rejected. Student can re-enroll for this course.
                  </p>
                  {drawer.reviewNote && (
                    <p style={{ color: 'var(--muted)', fontSize: 12, fontStyle: 'italic', margin: 0 }}>"{drawer.reviewNote}"</p>
                  )}
                </div>
              )}

              {drawer.status === 'completed' && (() => {
                const shotCount = (drawer.taskSubmissions || []).reduce((n, t) => n + (t.screenshots?.length || 0), 0);
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <p style={{ textAlign: 'center', color: '#10B981', fontSize: 13, fontWeight: 700, margin: 0 }}>
                      Certificate issued. Internship complete.
                    </p>

                    {drawer.screenshotsCleanedAt ? (
                      <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12, margin: 0 }}>
                        🗑 Screenshots cleaned up on {new Date(drawer.screenshotsCleanedAt).toLocaleDateString('en-IN')}
                      </p>
                    ) : shotCount > 0 ? (
                      <button
                        disabled={actionLoading}
                        onClick={() => handleCleanupScreenshots(drawer._id)}
                        style={{ width: '100%', padding: '12px', background: 'rgba(248,113,113,0.08)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', opacity: actionLoading ? 0.5 : 1 }}
                      >
                        {actionLoading ? 'Cleaning up…' : `🗑 Free Up Storage — Delete ${shotCount} Screenshot${shotCount === 1 ? '' : 's'}`}
                      </button>
                    ) : null}
                  </div>
                );
              })()}
            </div>

          </div>
        </div>
      )}
      {showReview && drawer && (
        <ReviewModal
          app={drawer}
          onClose={() => setShowReview(false)}
        />
      )}
    </>
  );
};

export default AdminMicrointernships;
